import {
  Box,
  Grid,
  Typography,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import Fuse from "fuse.js";
import { IndustrialEquipment } from "../../api-models/IndustrialEquipment";
import { loadEquipmentData } from "../../data/equipmentData";
import { ProductCard } from "../ProductCard/ProductCard";
import { ProductFilters } from "../ProductFilters/ProductFilters";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  subtitle: {
    color: theme.palette.text.secondary,
  },
  grid: {
    marginTop: theme.spacing(2),
  },
  resultCount: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  noResults: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  fuzzyMatch: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius,
    fontSize: "0.875rem",
  },
}));

interface ProductCatalogProps {
  descriptor?: any;
  outputport?: any;
}

export const ProductCatalog = ({ outputport }: ProductCatalogProps) => {
  const classes = useStyles();
  const [equipmentData, setEquipmentData] = useState<IndustrialEquipment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filteredEquipment, setFilteredEquipment] = useState<
    IndustrialEquipment[]
  >([]);
  const [fuzzySearchApplied, setFuzzySearchApplied] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [preselectedClass, setPreselectedClass] = useState<string | undefined>(
    undefined
  );
  const [displayCount, setDisplayCount] = useState(24); // Initial load limit

  // Normalize text and sort words for order-independent matching
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[&,.-]/g, " ") // Replace punctuation with spaces
      .replace(/\s+/g, " ") // Collapse multiple spaces
      .trim()
      .split(" ")
      .sort()
      .join(" ");
  };

  // Load equipment data from external JSON file (not bundled)
  useEffect(() => {
    let mounted = true;

    loadEquipmentData().then((data) => {
      if (mounted) {
        setEquipmentData(data);
        setFilteredEquipment(data);
        setIsLoading(false);
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Memoize Fuse instance for large datasets with custom getFn
  const fuseInstance = useMemo(() => {
    if (equipmentData.length === 0) return null;
    return new Fuse(equipmentData, {
      keys: ["requirementsClass.name"],
      threshold: 0.3,
      includeScore: true,
      ignoreLocation: true,
      getFn: (obj: any, path: string | string[]) => {
        const value = Fuse.config.getFn(obj, path);
        return typeof value === "string" ? normalizeText(value) : value;
      },
    });
  }, [equipmentData]);

  // Applica fuzzy search se presente outputport.name
  useEffect(() => {
    if (!fuseInstance || equipmentData.length === 0) return;

    if (outputport?.name) {
      const outputportName = outputport.name;
      setSearchTerm(outputportName);

      const normalizedSearch = normalizeText(outputportName);
      console.log("=== FUZZY SEARCH DEBUG ===");
      console.log("Original search:", outputportName);
      console.log("Normalized search:", normalizedSearch);

      const results = fuseInstance.search(normalizedSearch);
      console.log("Fuse results:", results.length, results.slice(0, 3));

      if (results.length > 0) {
        const matched = results.map((r) => r.item);
        setFilteredEquipment(matched);
        setFuzzySearchApplied(true);

        // Preimposta il filtro Class con la classe che ha matchato
        const matchedClassName = matched[0]?.requirementsClass?.name;
        if (matchedClassName) {
          setPreselectedClass(matchedClassName);
        }

        console.log(
          `Fuzzy search applied for "${outputportName}": ${matched.length} matches found`
        );
      } else {
        console.log(`Fuzzy search for "${outputportName}" found no matches.`);
        // Nessun match, mostra tutti
        setFilteredEquipment(equipmentData);
        setFuzzySearchApplied(false);
        setPreselectedClass(undefined);
      }
    } else {
      // Nessun outputport, mostra tutti
      setFilteredEquipment(equipmentData);
      setFuzzySearchApplied(false);
      setPreselectedClass(undefined);
    }
  }, [outputport, fuseInstance, equipmentData]);

  const handleProductClick = (equipment: IndustrialEquipment) => {
    console.log("Product clicked:", equipment);
    // Qui puoi aggiungere la logica per mostrare i dettagli del prodotto
  };

  // Infinite scroll handler
  const handleLoadMore = useCallback(() => {
    setDisplayCount((prev) => Math.min(prev + 24, filteredEquipment.length));
  }, [filteredEquipment.length]);

  // Displayed equipment with limit
  const displayedEquipment = useMemo(
    () => filteredEquipment.slice(0, displayCount),
    [filteredEquipment, displayCount]
  );

  // Ref for infinite scroll trigger using Intersection Observer
  const observerRef = useRef<IntersectionObserver | null>(null);

  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      // Cleanup previous observer
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (!node) return;

      // Create new observer
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting &&
            displayCount < filteredEquipment.length
          ) {
            handleLoadMore();
          }
        },
        {
          root: null, // viewport
          rootMargin: "200px", // trigger 200px before reaching the element
          threshold: 0.1,
        }
      );

      observerRef.current.observe(node);
    },
    [displayCount, filteredEquipment.length, handleLoadMore]
  );

  if (isLoading) {
    return (
      <Box
        className={classes.container}
        style={{ display: "flex", justifyContent: "center", padding: "4rem" }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h4" className={classes.title}>
          EQHub Industrial Equipment Catalog
        </Typography>
        <Typography variant="body1" className={classes.subtitle}>
          Browse our comprehensive catalog of industrial equipment and
          components
        </Typography>
      </Box>

      {fuzzySearchApplied && (
        <Box className={classes.fuzzyMatch}>
          <Typography variant="body2">
            🔍 <strong>Fuzzy search active:</strong> Showing products matching "
            {searchTerm}" (70% similarity threshold)
          </Typography>
        </Box>
      )}

      <ProductFilters
        equipment={fuzzySearchApplied ? filteredEquipment : equipmentData}
        onFilteredDataChange={setFilteredEquipment}
        preselectedClass={preselectedClass}
      />

      <Typography variant="body2" className={classes.resultCount}>
        Showing {displayedEquipment.length} of {filteredEquipment.length}{" "}
        products
        {filteredEquipment.length !== equipmentData.length &&
          ` (${equipmentData.length} total)`}
      </Typography>

      {filteredEquipment.length > 0 ? (
        <>
          <Grid container spacing={3} className={classes.grid}>
            {displayedEquipment.map((equipment) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={equipment.id}>
                <ProductCard
                  equipment={equipment}
                  onClick={() => handleProductClick(equipment)}
                />
              </Grid>
            ))}
          </Grid>
          {displayCount < filteredEquipment.length && (
            <div
              ref={loadMoreRef}
              style={{
                display: "flex",
                justifyContent: "center",
                padding: "2rem",
              }}
            >
              <CircularProgress size={32} />
            </div>
          )}
        </>
      ) : (
        <Box className={classes.noResults}>
          <Typography variant="h6">No products found</Typography>
          <Typography variant="body2">
            Try adjusting your filters to see more results
          </Typography>
        </Box>
      )}
    </Box>
  );
};
